import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useAllUsers from './useAllUsers';
import usePagination from './usePagination';
import roleCheck from '../../utils/roleCheck';
import { ROLES } from '../../utils/roles';
import { URLs } from '../../Constants/Consts';
import { fetchApi } from '../Functions';
import { addUsers } from '../../store/Users';
import { RootState } from '../../store/store';
import { AssignmentApiData, UserAssignment } from '../../types/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const ROWS_PER_PAGE = 25;

/** Searchable fields on a UserAssignment row */
const SEARCHABLE_FIELDS: (keyof UserAssignment)[] = [
    'fullName', 'kionEmail', 'discipline', 'project',
    'sequence', 'loginId', 'projectNumber', 'disciplineId',
];

export default function useUserManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();

    // --- Auth / role ---
    const { role: userRole } = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const isAdminUser = roleCheck(userRole, [ROLES.SuperAdmin, ROLES.Admin, ROLES.AdminManager]);
    const canAddUser = roleCheck(userRole, [ROLES.SuperAdmin]);

    // --- Redux selectors ---
    const disciplines = useSelector((state: RootState) => state.Discipline.allDisciplines);
    const projects = useSelector((state: RootState) => state.Project.projects);
    const accessLevels = useSelector((state: RootState) => state.AccessLevel.AccessLevels);
    const { Users } = useSelector((state: RootState) => state.UserAssignment);

    // --- Local state ---
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);

    // --- React-table instance ---
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useAllUsers();

    // --- Shared fetch + map helper ---
    const fetchAndMapUsers = useCallback(async (): Promise<UserAssignment[]> => {
        const usersAssignments: AssignmentApiData[] = await fetchApi(URLs.getAssignmentsUrl()).then(res => res.json());
        return usersAssignments.map(ua => {
            // Use accessLevelName from assignment as role
            const accessLevelName = accessLevels.find(al => al.accessLevelId === ua.accessLevelId)?.accessLevelName;
            return {
                ...ua,
                project: projects.find(p => p.projectNumber === ua.projectNumber)?.projectName ?? '',
                discipline: disciplines?.find(d => d.disciplineId === ua.disciplineId)?.disciplineName ?? '',
                accessLevelName: accessLevelName,
                role: accessLevelName ?? 'User', // Use accessLevelName as the role
            };
        }) as UserAssignment[];
    }, [projects, disciplines, accessLevels]);

    // --- Initial load ---
    useEffect(() => {
        const loadUsers = async () => {
            if (
                hasFetchedRef.current ||
                Users.length > 0 ||
                projects.length === 0 ||
                disciplines.length === 0 ||
                !navigator.onLine
            ) return;

            try {
                hasFetchedRef.current = true;
                setIsInitialLoading(true);
                setFetchError(null);
                const tableData = await fetchAndMapUsers();
                dispatch(addUsers(tableData));
            } catch (error) {
                console.error('Failed to fetch users:', error);
                setFetchError('Failed to load users. Please try refreshing.');
                hasFetchedRef.current = false;
            } finally {
                setIsInitialLoading(false);
            }
        };

        loadUsers();
    }, [Users.length, projects, disciplines, dispatch, fetchAndMapUsers]);

    // --- Refresh handler ---
    const handleRefresh = useCallback(async () => {
        if (!navigator.onLine || projects.length === 0 || disciplines.length === 0) return;

        setIsRefreshing(true);
        setFetchError(null);
        try {
            const tableData = await fetchAndMapUsers();
            dispatch(addUsers(tableData));
        } catch (error) {
            console.error('Failed to refresh users:', error);
            setFetchError('Failed to refresh users. Please try again.');
        } finally {
            setIsRefreshing(false);
        }
    }, [dispatch, projects.length, disciplines.length]);

    // --- Filtered rows ---
    const filteredRows = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return rows.filter(row => {
            if (!row.original) return false;
            const rowData = row.original;

            if (params.projectName && !rowData.project?.includes(params.projectName)) {
                return false;
            }

            if (!term) return true;

            return SEARCHABLE_FIELDS.some(field => {
                const value = rowData[field];
                return value != null && String(value).toLowerCase().includes(term);
            });
        });
    }, [rows, searchTerm, params.projectName]);

    // --- Pagination ---
    const { currentPage, totalPages, currentRows, handleNextPage, handlePreviousPage, setCurrentPage } =
        usePagination(filteredRows, ROWS_PER_PAGE);

    // --- Search handler ---
    const handleSearch = useCallback((term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, [setCurrentPage]);

    // --- Dismiss error ---
    const dismissError = useCallback(() => setFetchError(null), []);

    // --- Navigation helpers ---
    const navigateToCreateUser = useCallback(() => navigate('/AllUsers/Create'), [navigate]);
    const navigateToEditUser = useCallback(
        (assignmentUuid: string) => navigate(`/AllUsers/Edit/${assignmentUuid}`),
        [navigate],
    );

    return {
        // Auth
        isAdminUser,
        canAddUser,

        // Loading & error state
        isInitialLoading,
        isRefreshing,
        fetchError,
        dismissError,

        // Search
        searchTerm,
        handleSearch,

        // Table
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,

        // Paginated data
        currentRows,
        currentPage,
        totalPages,
        handleNextPage,
        handlePreviousPage,

        // Actions
        handleRefresh,
        navigateToCreateUser,
        navigateToEditUser,
    };
}
